import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FileText, Gift, Star } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { catalogsAPI, productsAPI, offersAPI } from '../lib/api';
import { toast } from 'sonner';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PosterTab } from '../components/client/PosterTab';
import { OrderFromOfferDialog } from '../components/client/OrderFromOfferDialog';
import { CatalogsTab } from '../components/client/CatalogsTab';
import { MyOffersTab } from '../components/client/MyOffersTab';

export const ClientCatalogs = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { addItem, setOfferId } = useCart();
    const { user } = useAuth();
    const [catalogs, setCatalogs] = useState([]);
    const [weeklyOffers, setWeeklyOffers] = useState([]);
    const [myOffers, setMyOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'catalogs');
    const [customPrices, setCustomPrices] = useState({});
    const [offerCustomPrices, setOfferCustomPrices] = useState({});
    const [showPriceEditor, setShowPriceEditor] = useState(false);
    const [editingOfferId, setEditingOfferId] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [posterModel, setPosterModel] = useState('simple');

    // Order from offer states
    const [orderDialogOffer, setOrderDialogOffer] = useState(null);
    const [orderSelection, setOrderSelection] = useState({});
    const [allProducts, setAllProducts] = useState([]);
    const [extraSearch, setExtraSearch] = useState('');
    const [showExtraProducts, setShowExtraProducts] = useState(false);

    const openOrderDialog = async (offer) => {
        const sel = {};
        (offer.products || []).forEach(p => {
            sel[p.product_id] = { checked: true, qty: p.min_order || 1, product: p, fromOffer: true };
        });
        setOrderSelection(sel);
        setOrderDialogOffer(offer);
        setShowExtraProducts(false);
        setExtraSearch('');
        if (allProducts.length === 0) {
            try {
                const res = await productsAPI.getAll();
                setAllProducts(res.data?.products || []);
            } catch (err) { console.error('Error loading products for catalog:', err); }
        }
    };

    const toggleProduct = (pid) => {
        setOrderSelection(prev => ({ ...prev, [pid]: { ...prev[pid], checked: !prev[pid]?.checked } }));
    };

    const setProductQty = (pid, qty) => {
        if (qty < 1) qty = 1;
        setOrderSelection(prev => ({ ...prev, [pid]: { ...prev[pid], qty } }));
    };

    const addExtraProduct = (product) => {
        setOrderSelection(prev => ({
            ...prev,
            [product.id]: {
                checked: true, qty: product.min_order || 1,
                product: { product_id: product.id, product_name: product.name, price: product.price, unit: product.unit, image_url: product.image_url },
                fromOffer: false
            }
        }));
        toast.success(`${product.name} ajouté`);
    };

    const getOrderTotal = () => Object.values(orderSelection).filter(s => s.checked).reduce((sum, s) => sum + (s.product?.price || 0) * s.qty, 0);
    const getSelectedCount = () => Object.values(orderSelection).filter(s => s.checked).length;

    const submitOfferOrder = () => {
        const selected = Object.entries(orderSelection).filter(([, s]) => s.checked);
        if (selected.length === 0) { toast.error('Sélectionnez au moins un produit'); return; }
        selected.forEach(([pid, s]) => {
            addItem({
                id: s.product.product_id || pid, name: s.product.product_name || s.product.name,
                price: s.product.price, unit: s.product.unit, image_url: s.product.image_url || '',
                min_order: 1, ready_to_order: true,
            }, s.qty);
        });
        setOfferId(orderDialogOffer?.id || null);
        toast.success(`${selected.length} produit(s) ajouté(s) au panier commande`);
        setOrderDialogOffer(null);
        navigate('/commander');
    };

    const filteredExtraProducts = allProducts
        .filter(p => !extraSearch || p.name?.toLowerCase().includes(extraSearch.toLowerCase()))
        .filter(p => !orderSelection[p.id]?.fromOffer);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [catalogsRes, offersRes, myOffersRes] = await Promise.all([
                    catalogsAPI.listPublic(), productsAPI.getWeeklyOffersByCategory(), offersAPI.getMyOffers()
                ]);
                setCatalogs(catalogsRes.data.catalogs || []);
                setMyOffers(myOffersRes.data.offers || []);
                const allOffers = (offersRes.data.categories || []).flatMap(cat => cat.products || []);
                setWeeklyOffers(allOffers);
                const prices = {};
                allOffers.forEach(p => { prices[p.id] = p.price; });
                setCustomPrices(prices);
                const userData = JSON.parse(localStorage.getItem('prixdeal_user') || '{}');
                setCompanyName(userData.company_name || '');
                if (searchParams.get('tab') === 'offers') setActiveTab('offers');
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Erreur lors du chargement');
            } finally { setLoading(false); }
        };
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount, tab from searchParams
    }, [searchParams]);

    const handleDownload = (catalog) => {
        const token = localStorage.getItem('prixdeal_token');
        const url = `${catalogsAPI.getDownloadUrl(catalog.id)}?token=${token}`;
        const link = document.createElement('a');
        link.href = url; link.download = catalog.original_filename; link.target = '_blank';
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        toast.success('Téléchargement démarré');
    };

    const handlePriceChange = (productId, value) => {
        setCustomPrices(prev => ({ ...prev, [productId]: parseFloat(value) || 0 }));
    };

    const handleOfferPriceChange = (offerId, productId, value) => {
        setOfferCustomPrices(prev => ({
            ...prev,
            [offerId]: { ...(prev[offerId] || {}), [productId]: value === '' ? '' : parseFloat(value) || 0 }
        }));
    };

    const getOfferProductPrice = (offerId, product) => {
        const customPrice = offerCustomPrices[offerId]?.[product.product_id];
        if (customPrice !== undefined && customPrice !== '') return customPrice;
        return product.price || product.offer_price || 0;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jungle"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand flex flex-col" data-testid="client-catalogs-page">
            <Header />
            <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
                <div className="mb-8">
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Documents & Promotions</h1>
                    <p className="text-muted-foreground mt-2">Téléchargez nos catalogues et créez vos propres affiches promotionnelles</p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid w-full max-w-xl grid-cols-3">
                        <TabsTrigger value="catalogs" className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />Catalogues ({catalogs.length})
                        </TabsTrigger>
                        <TabsTrigger value="offers" className="flex items-center gap-2 relative">
                            <Gift className="h-4 w-4" />Mes Offres
                            {myOffers.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5">{myOffers.length}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger value="posters" className="flex items-center gap-2">
                            <Star className="h-4 w-4" />Affiches Promo
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="catalogs" className="space-y-4">
                        <CatalogsTab catalogs={catalogs} onDownload={handleDownload} />
                    </TabsContent>

                    <TabsContent value="offers" className="space-y-6">
                        <MyOffersTab
                            myOffers={myOffers} user={user} editingOfferId={editingOfferId}
                            setEditingOfferId={setEditingOfferId} offerCustomPrices={offerCustomPrices}
                            onOfferPriceChange={handleOfferPriceChange} getOfferProductPrice={getOfferProductPrice}
                            companyName={companyName} setCompanyName={setCompanyName} posterModel={posterModel}
                            onOpenOrderDialog={openOrderDialog}
                        />
                    </TabsContent>

                    <TabsContent value="posters" className="space-y-6">
                        <PosterTab weeklyOffers={weeklyOffers} customPrices={customPrices} handlePriceChange={handlePriceChange}
                            companyName={companyName} setCompanyName={setCompanyName} posterModel={posterModel} setPosterModel={setPosterModel}
                            showPriceEditor={showPriceEditor} setShowPriceEditor={setShowPriceEditor} />
                    </TabsContent>
                </Tabs>

                <OrderFromOfferDialog orderDialogOffer={orderDialogOffer} setOrderDialogOffer={setOrderDialogOffer}
                    orderSelection={orderSelection} toggleProduct={toggleProduct} setProductQty={setProductQty}
                    addExtraProduct={addExtraProduct} showExtraProducts={showExtraProducts} setShowExtraProducts={setShowExtraProducts}
                    extraSearch={extraSearch} setExtraSearch={setExtraSearch} filteredExtraProducts={filteredExtraProducts}
                    getOrderTotal={getOrderTotal} getSelectedCount={getSelectedCount} submitOfferOrder={submitOfferOrder} />
            </main>
            <Footer />
        </div>
    );
};
